/**
 * Blog Sorting Functionality
 * Handles client-side sorting of blog articles
 */
class BlogSorting {
  constructor() {
    this.articleGrid = null;
    this.articles = [];
    this.currentSort = 'published-desc';

    this.init();
  }

  init() {
    // Get the article grid
    this.articleGrid = document.querySelector('#blog-article-grid');
    if (!this.articleGrid) {
      console.warn('[BlogSorting] Article grid not found');
      return;
    }

    // Get current sort from URL
    const urlParams = new URLSearchParams(window.location.search);
    this.currentSort = urlParams.get('sort_by') || 'published-desc';

    // Store original articles data
    this.storeArticlesData();

    // Apply initial sort
    this.sortArticles(this.currentSort);

    // Attach event listeners to sort links
    this.attachSortListeners();
  }

  storeArticlesData() {
    const articleItems = this.articleGrid.querySelectorAll('li.article-item');

    articleItems.forEach((item) => {
      const title = item.getAttribute('data-article-title') || '';
      const dateStr = item.getAttribute('data-article-date');
      const date = dateStr ? new Date(dateStr) : new Date();

      this.articles.push({
        element: item,
        title: title,
        date: date,
        originalClass: item.className,
        isLarge: item.classList.contains('article-large')
      });
    });
  }

  sortArticles(sortType) {
    let sortedArticles = [...this.articles];

    switch (sortType) {
      case 'title-asc':
        sortedArticles.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-desc':
        sortedArticles.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'published-asc':
        sortedArticles.sort((a, b) => a.date - b.date);
        break;
      case 'published-desc':
      default:
        sortedArticles.sort((a, b) => b.date - a.date);
        break;
    }

    // Clear the grid
    this.articleGrid.innerHTML = '';

    // Re-append articles in sorted order and maintain grid pattern
    sortedArticles.forEach((article, index) => {
      // Calculate if this position should be large (1st and 7th positions in each set of 7)
      const position = (index % 7) + 1;
      const shouldBeLarge = (position === 1 || position === 7);

      // Update the class to match the grid pattern
      const element = article.element;
      element.className = article.originalClass;

      if (shouldBeLarge && !element.classList.contains('article-large')) {
        element.classList.add('article-large');
      } else if (!shouldBeLarge && element.classList.contains('article-large')) {
        element.classList.remove('article-large');
      }

      this.articleGrid.appendChild(element);
    });

    // Update current sort
    this.currentSort = sortType;
  }

  attachSortListeners() {
    const sortLinks = document.querySelectorAll('.facet-checkbox[data-sort-value]');

    sortLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();

        const sortValue = link.getAttribute('data-sort-value');

        // Sort the articles
        this.sortArticles(sortValue);

        // Update URL without page reload
        const url = new URL(window.location);
        url.searchParams.set('sort_by', sortValue);
        window.history.pushState({}, '', url);

        // Update active state
        document.querySelectorAll('.facet-checkbox').forEach(l => l.classList.remove('active'));
        link.classList.add('active');

        // Update the displayed sort label
        this.updateSortLabel(sortValue);

        // Close the dropdown
        const details = link.closest('details');
        if (details) {
          details.removeAttribute('open');
        }
      });
    });
  }

  updateSortLabel(sortValue) {
    const label = document.getElementById('current-sort-label');
    if (!label) return;

    const labels = {
      'title-asc': 'A-Z',
      'title-desc': 'Z-A',
      'published-asc': 'Old to New',
      'published-desc': 'New to Old'
    };

    label.textContent = labels[sortValue] || 'New to Old';
  }

  // Method to reinitialize after filter changes or infinite scroll
  reinitialize() {
    this.articles = [];
    this.articleGrid = document.querySelector('#blog-article-grid');

    if (this.articleGrid) {
      this.storeArticlesData();
      this.sortArticles(this.currentSort);
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.blogSorting = new BlogSorting();
  });
} else {
  window.blogSorting = new BlogSorting();
}

// Listen for infinite scroll updates
window.addEventListener('blogArticlesLoaded', () => {
  if (window.blogSorting) {
    window.blogSorting.reinitialize();
  }
});
